import { useEffect, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import {
  Mail,
  Send,
  FlaskConical,
  Plus,
  Trash2,
  Users,
  GripVertical,
  Heading1,
  Type,
  MousePointerClick,
  Minus,
  MessageSquareQuote,
  Image as ImageIcon,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { uploadImage } from "@/lib/upload";

const TEMPLATES = [
  { id: "annuncio", label: "Annuncio", desc: "Accento oro, per novità e comunicazioni", color: "#D4AF37" },
  { id: "sorpresa", label: "Sorpresa", desc: "Accento rosso, spotlight sui partner", color: "#B82E3E" },
  { id: "promemoria", label: "Promemoria", desc: "Accento verde, countdown e reminder", color: "#2E7D5B" },
];

const BLOCK_DEFS = [
  { type: "heading", label: "Titolo", icon: Heading1 },
  { type: "text", label: "Testo", icon: Type },
  { type: "button", label: "Pulsante", icon: MousePointerClick },
  { type: "image", label: "Immagine", icon: ImageIcon },
  { type: "callout", label: "Box", icon: MessageSquareQuote },
  { type: "divider", label: "Divisore", icon: Minus },
];

const newBlock = (type) => ({
  key: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  type,
  text: type === "heading" ? "Titolo di sezione" : type === "button" ? "Scopri di più" : "",
  url: type === "button" ? window.location.origin : "",
});

const defaultBlocks = () => [
  { ...newBlock("text"), text: "Scrivi qui il corpo della tua newsletter. Ogni riga diventa un paragrafo." },
];

const inputCls =
  "w-full rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-2.5 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none focus:border-amber-400/70";

const BlockCard = ({ block, index, onRemove, onUpload, uploading, children }) => {
  const controls = useDragControls();
  const def = BLOCK_DEFS.find((d) => d.type === block.type);
  return (
    <Reorder.Item
      value={block}
      dragListener={false}
      dragControls={controls}
      data-testid={`block-${index}-${block.type}`}
      className="rounded-xl border border-amber-500/20 bg-[#0C241A] p-4"
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-300/80">
          <button
            type="button"
            data-testid={`block-drag-${index}`}
            aria-label="Trascina per riordinare"
            onPointerDown={(e) => controls.start(e)}
            className="cursor-grab touch-none rounded-md p-1 text-emerald-100/50 transition-colors hover:text-amber-200 active:cursor-grabbing"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          {def && <def.icon className="h-3.5 w-3.5" />} {def?.label}
        </span>
        <button
          type="button"
          data-testid={`block-delete-${index}`}
          onClick={onRemove}
          aria-label="Elimina blocco"
          className="rounded-md p-1.5 text-red-300/70 hover:bg-red-400/10 hover:text-red-300"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {children}
    </Reorder.Item>
  );
};

export const NewsletterTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [template, setTemplate] = useState("annuncio");
  const [title, setTitle] = useState("");
  const [preheader, setPreheader] = useState("");
  const [blocks, setBlocks] = useState(defaultBlocks());
  const [previewHtml, setPreviewHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/admin/campaigns"), api.get("/admin/subscribers")]);
      setCampaigns(c.data);
      setSubscribers(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadPreview = async (id) => {
    try {
      const { data } = await api.get(`/admin/campaigns/${id}/preview`);
      setPreviewHtml(data.html);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const selectCampaign = (c) => {
    setSelected(c);
    setTemplate(c.template);
    setTitle(c.title);
    setPreheader(c.preheader || "");
    const source = c.blocks?.length ? c.blocks : [{ type: "text", text: c.body || "" }];
    setBlocks(source.map((b) => ({ ...b, key: `${b.type}-${Math.random().toString(36).slice(2, 9)}` })));
    loadPreview(c.id);
  };

  const newCampaign = () => {
    setSelected(null);
    setTemplate("annuncio");
    setTitle("");
    setPreheader("");
    setBlocks(defaultBlocks());
    setPreviewHtml("");
  };

  const updateBlock = (key, field, value) =>
    setBlocks((bs) => bs.map((b) => (b.key === key ? { ...b, [field]: value } : b)));

  const removeBlock = (key) => setBlocks((bs) => bs.filter((b) => b.key !== key));

  const handleBlockUpload = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const url = await uploadImage(file);
      updateBlock(key, "url", url);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err, "Caricamento non riuscito."));
    } finally {
      setUploadingKey(null);
    }
  };

  const payload = () => ({
    template,
    title,
    preheader,
    blocks: blocks.map(({ key, ...rest }) => rest),
  });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (selected) {
        await api.put(`/admin/campaigns/${selected.id}`, payload());
        toast.success("Bozza aggiornata");
        await load();
        loadPreview(selected.id);
      } else {
        const { data } = await api.post("/admin/campaigns", payload());
        toast.success("Bozza creata");
        await load();
        setSelected(data);
        loadPreview(data.id);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/campaigns/${selected.id}/send-test`);
      toast.success(`Email di test inviata a ${data.to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendAll = async () => {
    if (!window.confirm(`Inviare la campagna a ${subscribers.length} iscritti?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/campaigns/${selected.id}/send`);
      toast.success(`Campagna inviata a ${data.sent} iscritti`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeSubscriber = async (s) => {
    try {
      await api.delete(`/admin/subscribers/${s.id}`);
      toast.success("Iscritto rimosso");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const canSend = selected && selected.status === "bozza";

  return (
    <div className="grid gap-8 lg:grid-cols-5" data-testid="newsletter-tab">
      {/* left: builder */}
      <div className="space-y-6 lg:col-span-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-amber-100">
            {selected ? `Campagna: ${selected.title}` : "Nuova campagna"}
          </h3>
          <button
            type="button"
            data-testid="campaign-new-button"
            onClick={newCampaign}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400/40 px-3 py-1.5 text-xs font-semibold text-amber-200 hover:bg-amber-400/10"
          >
            <Plus className="h-3.5 w-3.5" /> Nuova
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`template-${t.id}`}
              onClick={() => setTemplate(t.id)}
              className={`rounded-xl border p-3.5 text-left transition-colors ${
                template === t.id ? "border-amber-400/70 bg-amber-400/10" : "border-amber-500/20 hover:border-amber-500/40"
              }`}
            >
              <span className="block h-1.5 w-8 rounded-full" style={{ backgroundColor: t.color }} />
              <p className="mt-2.5 text-sm font-semibold text-amber-100">{t.label}</p>
              <p className="mt-1 text-[11px] leading-snug text-emerald-100/55">{t.desc}</p>
            </button>
          ))}
        </div>

        <form onSubmit={save} className="space-y-4" data-testid="campaign-form">
          <input
            data-testid="campaign-title-input"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Oggetto della email"
            className={inputCls}
          />
          <input
            data-testid="campaign-preheader-input"
            value={preheader}
            onChange={(e) => setPreheader(e.target.value)}
            placeholder="Preheader (anteprima nella casella di posta, opzionale)"
            className={inputCls}
          />

          {/* block builder with drag & drop */}
          <Reorder.Group
            axis="y"
            values={blocks}
            onReorder={setBlocks}
            className="space-y-3"
            data-testid="blocks-builder"
          >
            {blocks.map((block, index) => (
              <BlockCard
                key={block.key}
                block={block}
                index={index}
                uploading={uploadingKey === block.key}
                onRemove={() => removeBlock(block.key)}
              >
                {(block.type === "heading" || block.type === "button") && (
                  <input
                    data-testid={`block-text-${index}`}
                    value={block.text}
                    onChange={(e) => updateBlock(block.key, "text", e.target.value)}
                    placeholder={block.type === "heading" ? "Titolo di sezione" : "Testo del pulsante"}
                    className={inputCls}
                  />
                )}
                {(block.type === "text" || block.type === "callout") && (
                  <textarea
                    data-testid={`block-text-${index}`}
                    rows={block.type === "callout" ? 3 : 5}
                    value={block.text}
                    onChange={(e) => updateBlock(block.key, "text", e.target.value)}
                    placeholder={block.type === "callout" ? "Testo in evidenza nel box colorato" : "Testo del paragrafo. Ogni riga diventa un paragrafo."}
                    className={`${inputCls} resize-y`}
                  />
                )}
                {block.type === "button" && (
                  <input
                    data-testid={`block-url-${index}`}
                    value={block.url}
                    onChange={(e) => updateBlock(block.key, "url", e.target.value)}
                    placeholder="Link del pulsante (https del sito)"
                    className={`${inputCls} mt-2`}
                  />
                )}
                {block.type === "image" && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        data-testid={`block-url-${index}`}
                        value={block.url}
                        onChange={(e) => updateBlock(block.key, "url", e.target.value)}
                        placeholder="URL immagine (https://...)"
                        className={`${inputCls} flex-1`}
                      />
                      <label
                        data-testid={`block-upload-label-${index}`}
                        className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-xl border border-amber-400/40 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-400/10"
                      >
                        <Upload className="h-4 w-4" /> {uploadingKey === block.key ? "..." : "Carica"}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          data-testid={`block-upload-${index}`}
                          onChange={(e) => handleBlockUpload(block.key, e.target.files?.[0])}
                        />
                      </label>
                    </div>
                    {block.url && (
                      <img src={block.url} alt="" className="h-24 rounded-lg border border-amber-500/20 object-cover" />
                    )}
                  </div>
                )}
                {block.type === "divider" && (
                  <div className="border-t border-dashed border-amber-500/30 py-1 text-center text-[11px] uppercase tracking-widest text-emerald-100/40">
                    linea divisoria
                  </div>
                )}
              </BlockCard>
            ))}
          </Reorder.Group>

          {/* add block toolbar */}
          <div className="flex flex-wrap gap-2" data-testid="add-block-toolbar">
            {BLOCK_DEFS.map((d) => (
              <button
                key={d.type}
                type="button"
                data-testid={`add-block-${d.type}`}
                onClick={() => setBlocks((bs) => [...bs, newBlock(d.type)])}
                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-amber-400/40 px-3 py-2 text-xs font-semibold text-amber-200/80 transition-colors hover:border-amber-400/70 hover:text-amber-200"
              >
                <Plus className="h-3 w-3" /> {d.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="submit"
              data-testid="campaign-save-button"
              disabled={busy || (selected && selected.status === "inviata")}
              className="rounded-xl border border-amber-400/50 px-5 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-400/10 disabled:opacity-50"
            >
              {selected ? "Salva e aggiorna anteprima" : "Crea bozza"}
            </button>
            {canSend && (
              <>
                <button
                  type="button"
                  data-testid="campaign-test-button"
                  disabled={busy}
                  onClick={sendTest}
                  className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/40 px-5 py-2.5 text-sm font-semibold text-emerald-200 hover:bg-emerald-400/10 disabled:opacity-50"
                >
                  <FlaskConical className="h-4 w-4" /> Invia test a me
                </button>
                <button
                  type="button"
                  data-testid="campaign-send-button"
                  disabled={busy || subscribers.length === 0}
                  onClick={sendAll}
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 px-5 py-2.5 text-sm font-bold text-[#1A1204] disabled:opacity-50"
                >
                  <Send className="h-4 w-4" /> Invia a {subscribers.length} iscritti
                </button>
              </>
            )}
            {selected?.status === "inviata" && (
              <span className="rounded-full bg-emerald-400/15 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-emerald-300">
                Inviata a {selected.recipients} iscritti il {new Date(selected.sent_at).toLocaleDateString("it-IT")}
              </span>
            )}
          </div>
        </form>

        {previewHtml && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-amber-300/80">Anteprima email</p>
            <iframe
              title="Anteprima campagna"
              data-testid="campaign-preview"
              sandbox=""
              srcDoc={previewHtml}
              className="h-96 w-full rounded-2xl border border-amber-500/25 bg-white"
            />
          </div>
        )}
      </div>

      {/* right: lists */}
      <div className="space-y-6 lg:col-span-2">
        <div className="rounded-2xl border border-amber-500/20 bg-[#0C241A]/50 p-5">
          <h4 className="flex items-center gap-2 font-display text-base font-bold text-amber-100">
            <Mail className="h-4 w-4 text-amber-300" /> Campagne
          </h4>
          <ul className="mt-4 space-y-2" data-testid="campaigns-list">
            {campaigns.length === 0 && <li className="text-sm text-emerald-100/50">Nessuna campagna ancora.</li>}
            {campaigns.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  data-testid={`campaign-item-${c.id}`}
                  onClick={() => selectCampaign(c)}
                  className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                    selected?.id === c.id ? "border-amber-400/60 bg-amber-400/10" : "border-amber-500/15 hover:border-amber-500/35"
                  }`}
                >
                  <p className="truncate text-sm font-semibold text-amber-100">{c.title}</p>
                  <p className="mt-0.5 text-xs text-emerald-100/55">
                    {c.status === "inviata" ? `Inviata · ${c.recipients} destinatari` : "Bozza"} · {new Date(c.created_at).toLocaleDateString("it-IT")}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-amber-500/20 bg-[#0C241A]/50 p-5">
          <h4 className="flex items-center gap-2 font-display text-base font-bold text-amber-100">
            <Users className="h-4 w-4 text-amber-300" /> Iscritti ({subscribers.length})
          </h4>
          <ul className="mt-4 max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="subscribers-list">
            {subscribers.length === 0 && <li className="text-sm text-emerald-100/50">Nessun iscritto ancora. Il modulo è nel footer della home.</li>}
            {subscribers.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-amber-500/10 px-3.5 py-2.5 text-sm">
                <span className="truncate text-emerald-100/85">{s.email}</span>
                <button
                  type="button"
                  data-testid={`subscriber-delete-${s.id}`}
                  onClick={() => removeSubscriber(s)}
                  className="shrink-0 rounded-md p-1.5 text-red-300/70 hover:bg-red-400/10 hover:text-red-300"
                  aria-label="Rimuovi"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
